import { t as __t } from "i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";

import DashboardHeader from "../../../components/header/DashboardHeader";
import DashboardEmployerSidebar from "../../../components/header/DashboardSidebar";
import MobileMenu from "../../../components/header/MobileMenu";
import CopyrightFooter from "../../../components/footer/employers/CopyrightFooter";
import BackPrevious from "../../../components/back-previous";
import SwalAlert from "../../../components/common/alert";

import {
  callGetContractByKey,
  callgetVehicleCaseIdioms,
  callgetVehicleCaseIdiomsByContract,
  callgetVehicleCaseIdiomsAllByContract,
} from "../../../api/contract/service";
import { callGetVehicleInsuranceClaiming } from "../../../api/insurances/service";
import {
  decodeBase64,
  encodeBase64,
  FormatCurrencys,
  dateToString,
} from "../../../utils/commonUtil";
import { useListRefreshListener } from "../../../utils/listNavigation";

import "./index.css";

const EMPTY_SUMMARY = {
  caseCount: 0,
  claimCount: 0,
  requestedAmount: 0,
  approvedAmount: 0,
  paidAmount: 0,
};

const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.result)) return value.result;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.data?.result)) return value.data.result;
  return [];
};

const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const getClaimAmount = (claim) =>
  numberValue(
    claim?.claimAmount ??
      claim?.customerAmount ??
      claim?.targetAmount ??
      claim?.deathAmount ??
      claim?.hotpitalAmount ??
      claim?.editerAmount
  );

const getApprovedAmount = (claim) =>
  numberValue(
    claim?.approvedAmount ??
      claim?.approveAmount ??
      claim?.claimApproveAmount ??
      (Number(claim?.status) >= 2 ? getClaimAmount(claim) : 0)
  );

const getPaidAmount = (claim) =>
  numberValue(
    claim?.paidAmount ??
      claim?.paymentAmount ??
      (Number(claim?.status) === 3 ? getApprovedAmount(claim) : 0)
  );

const CLAIM_STATUS = {
  0: { labelKey: "claimPages.center.statuses.rejected", className: "danger", step: 1 },
  1: { labelKey: "claimPages.center.statuses.pending", className: "warning", step: 1 },
  2: { labelKey: "claimPages.center.statuses.approved", className: "success", step: 2 },
  3: { labelKey: "claimPages.center.statuses.paid", className: "paid", step: 3 },
};

const CASE_STATUS = {
  "-1": { labelKey: "claimPages.center.statuses.closed", className: "muted" },
  0: { labelKey: "claimPages.center.statuses.cancelled", className: "muted" },
  1: { labelKey: "claimPages.center.statuses.inProgress", className: "active" },
  2: { labelKey: "claimPages.center.statuses.completed", className: "done" },
};

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  try {
    return dateToString(value, withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
  } catch (error) {
    return moment(value).isValid()
      ? moment(value).format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY")
      : "-";
  }
};

const getCaseCode = (item) =>
  item?.caseIdiomsCode || item?.caseCode || item?.caseNo || "-";

const getClaimNo = (claim, index) =>
  claim?.claimNo || claim?.requestNo || claim?.claimCode || claim?.id || `#${index + 1}`;

const VehicleClaimCenter = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  const [contract, setContract] = useState(null);
  const [cases, setCases] = useState([]);
  const [claims, setClaims] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedCaseCode, setSelectedCaseCode] = useState("");
  const [loading, setLoading] = useState(true);

  const contractNumber = useMemo(() => {
    try {
      return decodeBase64(params?.contractNumber) || params?.contractNumber || "";
    } catch (error) {
      return params?.contractNumber || "";
    }
  }, [params?.contractNumber]);

  const loadContract = useCallback(async () => {
    if (!contractNumber) return null;

    const response = await dispatch(
      callGetContractByKey({
        keyword: contractNumber,
        page: 1,
        maxResult: 10,
        insuranceType: 2,
      })
    );

    if (response?.payload?.responseCode !== "00000") return null;

    const rows = toArray(response?.payload?.data);
    const exact =
      rows.find((item) => item?.contractNumber === contractNumber) || rows[0] || null;

    setContract(exact);
    return exact;
  }, [contractNumber, dispatch]);

  const loadCases = useCallback(async () => {
    if (!contractNumber) return [];

    let rows = [];

    // New backend endpoint: exact contract match and complete case history.
    try {
      const allResponse = await dispatch(
        callgetVehicleCaseIdiomsAllByContract({ contractNumber })
      );
      if (allResponse?.payload?.responseCode === "00000") {
        rows = toArray(allResponse?.payload?.data);
      }
    } catch (error) {
      // Continue to compatibility fallbacks below.
    }

    // Compatibility with an older backend that does not have getAllByContract.
    if (rows.length === 0) {
      const response = await dispatch(
        callgetVehicleCaseIdioms({
          keyword: contractNumber,
          contractNumber,
          page: 1,
          maxResult: 200,
        })
      );

      if (response?.payload?.responseCode === "00000") {
        rows = toArray(response?.payload?.data).filter(
          (item) =>
            !item?.contractNo ||
            item?.contractNo === contractNumber ||
            item?.contractNumber === contractNumber
        );
      }
    }

    if (rows.length === 0) {
      const fallback = await dispatch(
        callgetVehicleCaseIdiomsByContract({ contractNumber })
      );
      if (fallback?.payload?.responseCode === "00000") {
        const data = fallback?.payload?.data;
        rows = Array.isArray(data) ? data : data ? [data] : [];
      }
    }

    const unique = rows.filter(
      (item, index, array) =>
        array.findIndex(
          (candidate) =>
            String(candidate?.id ?? getCaseCode(candidate)) ===
            String(item?.id ?? getCaseCode(item))
        ) === index
    );

    setCases(unique);
    return unique;
  }, [contractNumber, dispatch]);

  const loadClaims = useCallback(async () => {
    if (!contractNumber) return [];

    const response = await dispatch(
      callGetVehicleInsuranceClaiming({
        contractNumber,
        status: "",
        fromDate: "",
        toDate: "",
        page: 1,
        maxResult: 500,
        sumCol: [
          "claim_amount",
          "customer_amount",
          "target_amount",
          "death_amount",
          "hotpital_amount",
          "editer_amount",
        ],
      })
    );

    const rows =
      response?.payload?.responseCode === "00000"
        ? toArray(response?.payload?.data)
        : [];

    setClaims(rows);
    return rows;
  }, [contractNumber, dispatch]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadContract(), loadCases(), loadClaims()]);
    } catch (error) {
      console.error("Vehicle claim center load error:", error);
      SwalAlert.error(
        t("claimPages.center.loadErrorTitle"),
        t("claimPages.center.loadErrorText")
      );
    } finally {
      setLoading(false);
    }
  }, [loadCases, loadClaims, loadContract]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Save/edit/approve child pages emit this event; plain Back does not.
  useListRefreshListener(loadAll);

  const claimsByCase = useMemo(() => {
    return claims.reduce((accumulator, claim) => {
      const key = claim?.caseIdiomsCode || claim?.caseCode || "__UNASSIGNED__";
      if (!accumulator[key]) accumulator[key] = [];
      accumulator[key].push(claim);
      return accumulator;
    }, {});
  }, [claims]);

  const summary = useMemo(() => {
    if (!cases.length && !claims.length) return EMPTY_SUMMARY;

    return {
      caseCount: cases.length,
      claimCount: claims.length,
      requestedAmount: claims.reduce(
        (total, item) => total + getClaimAmount(item),
        0
      ),
      approvedAmount: claims.reduce(
        (total, item) => total + getApprovedAmount(item),
        0
      ),
      paidAmount: claims.reduce((total, item) => total + getPaidAmount(item), 0),
    };
  }, [cases, claims]);

  const filteredClaims = useMemo(() => {
    const rows = selectedCaseCode
      ? claims.filter(
          (item) =>
            String(item?.caseIdiomsCode || item?.caseCode || "") ===
            String(selectedCaseCode)
        )
      : claims;

    return [...rows].sort((a, b) => {
      const aTime = new Date(a?.carriredDate || a?.createdAt || 0).getTime();
      const bTime = new Date(b?.carriredDate || b?.createdAt || 0).getTime();
      if (bTime !== aTime) return bTime - aTime;
      return Number(b?.id || 0) - Number(a?.id || 0);
    });
  }, [claims, selectedCaseCode]);

  const canCreate =
    contract?.status === 1 && Number(contract?.statusPayment) !== 3;

  const openNewCase = () => {
    if (!canCreate) {
      SwalAlert.error(
        t("claimPages.center.cannotOpenCase"),
        t("claimPages.center.checkContractPayment")
      );
      return;
    }

    navigate("case/save", {
      state: {
        returnToVehicleClaimCenter: true,
        contractNumber,
      },
    });
  };

  const openEditCase = (item) => {
    navigate(`case/save/${encodeBase64(item?.id)}`, {
      state: {
        returnToVehicleClaimCenter: true,
        contractNumber,
      },
    });
  };

  const openClaim = (caseItem) => {
    if (!canCreate) {
      SwalAlert.error(
        t("claimPages.center.cannotCreateClaim"),
        t("claimPages.center.checkContractPayment")
      );
      return;
    }

    if (Number(caseItem?.status) !== 1) {
      SwalAlert.error(
        t("claimPages.center.caseClosedTitle"),
        t("claimPages.center.caseClosedText")
      );
      return;
    }

    navigate(`clame/${encodeBase64(JSON.stringify(caseItem))}`, {
      state: {
        returnToVehicleClaimCenter: true,
        contractNumber,
        caseId: caseItem?.id,
      },
    });
  };

  const selectCaseClaims = (caseItem) => {
    setSelectedCaseCode(getCaseCode(caseItem));
    setActiveTab("claims");
  };

  const vehicleTitle =
    contract?.vehicleBrandName ||
    contract?.brandName ||
    contract?.vehicleBrand ||
    contract?.optionNameLa ||
    t("claimPages.center.vehicleInsurance");

  const vehicleSubtitle = [
    contract?.vehicleModel,
    contract?.licensePlate || contract?.vehicleLicensePlate,
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <div className="page-wrapper dashboard vehicle-claim-center-page">
      <span className="header-span" />
      <DashboardHeader />
      <MobileMenu />
      <DashboardEmployerSidebar />

      <section className="user-dashboard">
        <div className="dashboard-outer">
          <div className="vcc-page-head">
            <div className="vcc-page-head__left">
              <BackPrevious />
              <div>
                <span className="vcc-eyebrow">{t("claimPages.center.eyebrow")}</span>
                <h3>{t("claimPages.center.title")}</h3>
                <p>{t("claimPages.center.subtitle")}</p>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary vcc-new-case-btn"
              onClick={openNewCase}
              disabled={contract?.status !== 1}
            >
              <i className="fas fa-plus-circle" /> {t("claimPages.center.newCase")}
            </button>
          </div>

          <section className="vcc-contract-hero">
            <div className="vcc-contract-main">
              <div className="vcc-vehicle-icon">
                <i className="fas fa-car-side" />
              </div>
              <div>
                <span className="vcc-contract-number">
                  {contractNumber || "-"}
                </span>
                <h4>{vehicleTitle}</h4>
                <p>{vehicleSubtitle || contract?.fullName || "-"}</p>
              </div>
            </div>
            <div className="vcc-contract-meta">
              <div>
                <span>{t("claimPages.center.insured")}</span>
                <strong>{contract?.fullName || "-"}</strong>
              </div>
              <div>
                <span>{t("claimPages.center.signedDate")}</span>
                <strong>{formatDate(contract?.signedDate)}</strong>
              </div>
              <div>
                <span>{t("claimPages.center.status")}</span>
                <strong className={contract?.status === 1 ? "is-active" : "is-inactive"}>
                  {contract?.status === 1 ? t("claimPages.center.active") : t("claimPages.center.inactive")}
                </strong>
              </div>
            </div>
          </section>

          <section className="vcc-summary-grid">
            <SummaryCard icon="fa-folder-open" label={t("uiLabels.allCases")} value={summary.caseCount} />
            <SummaryCard icon="fa-file-medical" label={t("uiLabels.allClaims")} value={summary.claimCount} />
            <SummaryCard
              icon="fa-coins"
              label={t("uiLabels.claimTotal")}
              value={`${FormatCurrencys(summary.requestedAmount)} LAK`}
            />
            <SummaryCard
              icon="fa-check-circle"
              label={t("uiLabels.approved")}
              value={`${FormatCurrencys(summary.approvedAmount)} LAK`}
            />
            <SummaryCard
              icon="fa-money-bill-wave"
              label={t("uiLabels.paid")}
              value={`${FormatCurrencys(summary.paidAmount)} LAK`}
            />
          </section>

          <section className="vcc-workspace">
            <div className="vcc-tabs">
              <button
                type="button"
                className={activeTab === "overview" ? "active" : ""}
                onClick={() => setActiveTab("overview")}
              >
                <i className="fas fa-chart-pie" /> {t("claimPages.center.overview")}
              </button>
              <button
                type="button"
                className={activeTab === "cases" ? "active" : ""}
                onClick={() => setActiveTab("cases")}
              >
                <i className="fas fa-folder" /> {t("claimPages.center.cases")} ({cases.length})
              </button>
              <button
                type="button"
                className={activeTab === "claims" ? "active" : ""}
                onClick={() => {
                  setSelectedCaseCode("");
                  setActiveTab("claims");
                }}
              >
                <i className="fas fa-file-invoice-dollar" /> {__t("uiText.text381")}{claims.length})
              </button>
            </div>

            {loading ? (
              <div className="vcc-loading">
                <span className="spinner-border spinner-border-sm" />
                {t("claimPages.center.loading")}
              </div>
            ) : activeTab === "overview" ? (
              <OverviewTab
                cases={cases}
                claimsByCase={claimsByCase}
                openClaim={openClaim}
                selectCaseClaims={selectCaseClaims}
                openEditCase={openEditCase}
              />
            ) : activeTab === "cases" ? (
              <CasesTab
                cases={cases}
                claimsByCase={claimsByCase}
                openClaim={openClaim}
                selectCaseClaims={selectCaseClaims}
                openEditCase={openEditCase}
              />
            ) : (
              <ClaimsTab
                claims={filteredClaims}
                cases={cases}
                selectedCaseCode={selectedCaseCode}
                setSelectedCaseCode={setSelectedCaseCode}
              />
            )}
          </section>
        </div>
      </section>
      <CopyrightFooter />
    </div>
  );
};

const SummaryCard = ({ icon, label, value }) => (
  <article className="vcc-summary-card">
    <div className="vcc-summary-icon">
      <i className={`fas ${icon}`} />
    </div>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  </article>
);

const OverviewTab = ({
  cases,
  claimsByCase,
  openClaim,
  selectCaseClaims,
  openEditCase,
}) => {
  const { t } = useTranslation();
  return (
  <div className="vcc-section-body">
    <div className="vcc-section-title">
      <div>
        <h5>{t("claimPages.center.latestCases")}</h5>
        <p>{t("claimPages.center.latestCasesHelp")}</p>
      </div>
    </div>
    <CaseCards
      cases={cases.slice(0, 6)}
      claimsByCase={claimsByCase}
      openClaim={openClaim}
      selectCaseClaims={selectCaseClaims}
      openEditCase={openEditCase}
    />
  </div>
  );
};

const CasesTab = ({
  cases,
  claimsByCase,
  openClaim,
  selectCaseClaims,
  openEditCase,
}) => {
  const { t } = useTranslation();
  return (
  <div className="vcc-section-body">
    <div className="vcc-section-title">
      <div>
        <h5>{t("claimPages.center.allCaseList")}</h5>
        <p>{t("claimPages.center.allCaseListHelp")}</p>
      </div>
    </div>
    <CaseCards
      cases={cases}
      claimsByCase={claimsByCase}
      openClaim={openClaim}
      selectCaseClaims={selectCaseClaims}
      openEditCase={openEditCase}
    />
  </div>
  );
};

const CaseCards = ({
  cases,
  claimsByCase,
  openClaim,
  selectCaseClaims,
  openEditCase,
}) => {
  const { t } = useTranslation();
  if (!cases.length) {
    return <EmptyState icon="fa-folder-open" text={t("claimPages.center.noCases")} />;
  }

  return (
    <div className="vcc-case-grid">
      {cases.map((item, index) => {
        const code = getCaseCode(item);
        const relatedClaims = claimsByCase[code] || [];
        const requested = relatedClaims.reduce(
          (total, claim) => total + getClaimAmount(claim),
          0
        );
        const status = CASE_STATUS[item?.status] || {
          labelKey: null,
          label: item?.status ?? "-",
          className: "muted",
        };

        return (
          <article className="vcc-case-card" key={item?.id || code || index}>
            <div className="vcc-case-card__head">
              <div>
                <span className="vcc-case-label">{__t("uiText.text424")}</span>
                <h5>{code}</h5>
              </div>
              <span className={`vcc-case-status ${status.className}`}>
                {status.labelKey ? t(status.labelKey) : status.label}
              </span>
            </div>

            <div className="vcc-case-info-grid">
              <Info label={t("claimPages.center.incidentDate")} value={formatDate(item?.accidentDate, true)} />
              <Info
                label={t("claimPages.center.licensePlate")}
                value={item?.licensePlate || item?.vehicleInfo?.licensePlate || "-"}
              />
              <Info
                label={t("claimPages.center.location")}
                value={
                  item?.address ||
                  [item?.provinceName, item?.districtName, item?.village]
                    .filter(Boolean)
                    .join(" ") ||
                  "-"
                }
              />
              <Info label={t("claimPages.center.claimLabel")} value={t("claimPages.center.claimItems", { count: relatedClaims.length })} />
            </div>

            <div className="vcc-case-amount">
              <span>{t("claimPages.center.caseClaimTotal")}</span>
              <strong>{FormatCurrencys(requested)} LAK</strong>
            </div>

            {item?.accidentNote && (
              <p className="vcc-case-note">{item.accidentNote}</p>
            )}

            <div className="vcc-case-actions">
              <button type="button" className="btn btn-light" onClick={() => openEditCase(item)}>
                <i className="fas fa-pen" /> {t("claimPages.center.editCase")}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => selectCaseClaims(item)}
              >
                <i className="fas fa-list" /> {__t("uiText.text381")}{relatedClaims.length})
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => openClaim(item)}
                disabled={Number(item?.status) !== 1}
              >
                <i className="fas fa-plus" /> {t("claimPages.center.addClaim")}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
};

const ClaimsTab = ({
  claims,
  cases,
  selectedCaseCode,
  setSelectedCaseCode,
}) => {
  const { t } = useTranslation();
  return (
  <div className="vcc-section-body">
    <div className="vcc-section-title vcc-section-title--filter">
      <div>
        <h5>{t("claimPages.center.claimHistory")}</h5>
        <p>{t("claimPages.center.claimHistoryHelp")}</p>
      </div>
      <select
        className="form-select vcc-case-filter"
        value={selectedCaseCode}
        onChange={(event) => setSelectedCaseCode(event.target.value)}
      >
        <option value="">{t("claimPages.center.allCases")}</option>
        {cases.map((item, index) => (
          <option key={item?.id || index} value={getCaseCode(item)}>
            {getCaseCode(item)}
          </option>
        ))}
      </select>
    </div>

    {!claims.length ? (
      <EmptyState icon="fa-file-medical-alt" text={t("claimPages.center.noClaims")} />
    ) : (
      <div className="vcc-claim-list">
        {claims.map((claim, index) => (
          <ClaimCard claim={claim} index={index} key={claim?.id || index} />
        ))}
      </div>
    )}
  </div>
  );
};

const ClaimCard = ({ claim, index }) => {
  const { t } = useTranslation();
  const status = CLAIM_STATUS[claim?.status] || {
    labelKey: null,
    label: String(claim?.status ?? "-"),
    className: "muted",
    step: 0,
  };

  const caseCode = claim?.caseIdiomsCode || claim?.caseCode || t("claimPages.center.unassignedCase");
  const amount = getClaimAmount(claim);
  const approved = getApprovedAmount(claim);
  const paid = getPaidAmount(claim);

  return (
    <article className="vcc-claim-card">
      <div className="vcc-claim-card__head">
        <div>
          <span className="vcc-claim-case">{caseCode}</span>
          <h5>{__t("uiText.text425")}{getClaimNo(claim, index)}</h5>
          <p>{claim?.description || claim?.descriptionUser || claim?.claimCause || "-"}</p>
        </div>
        <span className={`vcc-claim-status ${status.className}`}>{status.labelKey ? t(status.labelKey) : status.label}</span>
      </div>

      <div className="vcc-claim-money-grid">
        <Money label={t("claimPages.center.requested")} value={amount} />
        <Money label={t("claimPages.center.approved")} value={approved} />
        <Money label={t("uiLabels.paid")} value={paid} />
        <Info label={t("claimPages.center.claimDate")} value={formatDate(claim?.carriredDate || claim?.createdAt)} />
      </div>

      {Number(claim?.status) === 0 && (claim?.descriptionUser || claim?.remark) && (
        <div className="vcc-reject-reason">
          <i className="fas fa-exclamation-circle" />
          <div>
            <strong>{t("claimPages.center.rejectReason")}</strong>
            <span>{claim?.descriptionUser || claim?.remark}</span>
          </div>
        </div>
      )}

      <ClaimProgress status={Number(claim?.status)} />
    </article>
  );
};

const ClaimProgress = ({ status }) => {
  const { t } = useTranslation();
  const rejected = status === 0;
  const activeStep = CLAIM_STATUS[status]?.step ?? 0;
  const steps = [
    { label: t("claimPages.center.steps.submitted"), icon: "fa-paper-plane" },
    { label: t("claimPages.center.steps.review"), icon: "fa-search" },
    { label: t("claimPages.center.steps.approved"), icon: "fa-check" },
    { label: t("claimPages.center.steps.paid"), icon: "fa-money-check-alt" },
  ];

  return (
    <div className={`vcc-progress ${rejected ? "is-rejected" : ""}`}>
      {steps.map((step, index) => {
        const completed = !rejected && index <= activeStep;
        const isRejectPoint = rejected && index === 1;
        return (
          <React.Fragment key={step.label}>
            <div
              className={`vcc-progress-step ${
                completed ? "completed" : isRejectPoint ? "rejected" : ""
              }`}
            >
              <span>
                <i className={`fas ${isRejectPoint ? "fa-times" : step.icon}`} />
              </span>
              <small>{isRejectPoint ? t("claimPages.center.statuses.rejected") : step.label}</small>
            </div>
            {index < steps.length - 1 && (
              <div className={`vcc-progress-line ${completed && index < activeStep ? "completed" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const Money = ({ label, value }) => (
  <div className="vcc-money-item">
    <span>{label}</span>
    <strong>{FormatCurrencys(value)} LAK</strong>
  </div>
);

const Info = ({ label, value }) => (
  <div className="vcc-info-item">
    <span>{label}</span>
    <strong>{value || "-"}</strong>
  </div>
);

const EmptyState = ({ icon, text }) => {
  const { t } = useTranslation();
  return (
    <div className="vcc-empty">
      <div><i className={`fas ${icon}`} /></div>
      <h5>{text}</h5>
      <p>{t("claimPages.center.startHelp")}</p>
    </div>
  );
};

export default VehicleClaimCenter;
